import type { NucleusServiceResponse } from '../types';
import type {
  CancelSubscriptionRequest,
  Subscription,
  SubscriptionPlan,
} from '@billx/types';

import { userApiClient } from '../clients';
import { handleAxiosError } from './utils';

export class SubscriptionService {
  /**
   * Create a new subscription
   */
  static async createSubscription(
    businessId: string,
    plan: SubscriptionPlan
  ): Promise<NucleusServiceResponse<Subscription>> {
    try {
      const response = await userApiClient.post('/subscriptions', {
        businessId,
        planId: plan.id,
      });

      return response.data;
    } catch (error: unknown) {
      return handleAxiosError(error, 'Failed to create subscription');
    }
  }

  /**
   * Get subscription
   */
  static async getCurrentSubscription(): Promise<
    NucleusServiceResponse<Subscription>
  > {
    try {
      const response = await userApiClient.get(`/subscriptions/current`);
      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch subscription');
    }
  }

  /**
   * Update subscription plan
   */
  static async updatePlanForSubscription(
    subscriptionId: string,
    plan: SubscriptionPlan
  ): Promise<NucleusServiceResponse<Subscription>> {
    try {
      const response = await userApiClient.put(
        `/subscriptions/${subscriptionId}`,
        {
          planId: plan.id,
        }
      );
      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to update subscription');
    }
  }

  /**
   * Purchase a subscription plan
   */
  static async subscribeToPlan(
    planId: string,
    payload?: { keepOutletIds?: string[]; keepUserIds?: string[] }
  ): Promise<
    NucleusServiceResponse<{
      checkoutUrl: string;
      pollingRequired: boolean;
      subscription?: Subscription;
    }>
  > {
    try {
      const response = await userApiClient.post(
        `/subscriptions/subscribe/${planId}`,
        payload || {}
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to purchase subscription plan');
    }
  }

  /**
   * Cancel subscription
   */
  static async cancelSubscription(
    subscriptionId: string,
    payload: CancelSubscriptionRequest
  ): Promise<NucleusServiceResponse<Subscription>> {
    try {
      const response = await userApiClient.patch(
        `/subscriptions/${subscriptionId}/cancel`,
        payload
      );
      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to cancel subscription');
    }
  }

  /**
   * Preview a plan change
   */
  static async previewPlanChange(
    planId: string,
    businessId: string
  ): Promise<
    NucleusServiceResponse<{
      paymentPreview: any;
      limitPreview: {
        outlets: { current: number; limit: number };
        users: { current: number; limit: number };
      };
    }>
  > {
    try {
      const params = new URLSearchParams();
      params.append('businessId', businessId);
      const response = await userApiClient.get(
        `/subscriptions/preview-change/${planId}`,
        { params }
      );
      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to preview plan change');
    }
  }
}
